import type { DatabaseFile } from '../types';
import type { GitHubService } from './GitHubService';
import type { MarkdownService } from './MarkdownService';

/** In-memory open files and original markdown for discard/serialize. */
export class AppFileRegistry {
  files: DatabaseFile[] = [];
  originalContents = new Map<string, string>();

  constructor(
    private readonly github: GitHubService,
    private readonly markdown: MarkdownService
  ) {}

  find(path: string): DatabaseFile | undefined {
    return this.files.find(f => f.path === path);
  }

  replaceAt(path: string, file: DatabaseFile): void {
    const idx = this.files.findIndex(f => f.path === path);
    if (idx >= 0) this.files[idx] = file;
    else this.files.push(file);
  }

  remove(path: string): void {
    const idx = this.files.findIndex(f => f.path === path);
    if (idx >= 0) this.files.splice(idx, 1);
    this.originalContents.delete(path);
  }

  async ensureLoaded(path: string): Promise<DatabaseFile> {
    const existing = this.find(path);
    if (existing) return existing;
    const { content, sha } = await this.github.readFile(path);
    this.originalContents.set(path, content);
    const loaded = this.markdown.parse(path, content, sha);
    this.files.push(loaded);
    return loaded;
  }

  async loadFromGithub(path: string): Promise<DatabaseFile> {
    const { content, sha } = await this.github.readFile(path);
    this.originalContents.set(path, content);
    const loaded = this.markdown.parse(path, content, sha);
    this.replaceAt(path, loaded);
    return loaded;
  }

  async refreshSha(file: DatabaseFile): Promise<void> {
    const { sha } = await this.github.readFile(file.path);
    file.sha = sha;
  }
}
